import queryString from "query-string";
import { PaletteColor, alpha } from "@mui/material";
import { VALID_MODES } from "../constants";
// Define the type for the payload sent to the parent window
interface PostMessagePayload {
  type: string;
  value: any;
}

// Post message to parent function with TypeScript type annotations
export const postMessageToParent = (type: string, value: any): void => {
  const payload: PostMessagePayload = { type, value };
  window.parent.postMessage(payload, "*");
};

// Function to extract configuration from the query string
export const extractConfig = (): Record<string, any> | null => {
  const { options } = queryString.parse(window.location.search);
  if (!options || typeof options !== "string") {
    console.warn(
      `Cannot decode option. Invalid format: ${typeof options}, expecting 'string'.`
    );
    return null;
  }

  try {
    const parsedOptions = JSON.parse(decodeURIComponent(options));
    if (parsedOptions.config && typeof parsedOptions.config === "object") {
      return { ...parsedOptions.config };
    } else {
      console.warn("Invalid configuration format.");
      return null;
    }
  } catch (err) {
    console.error(
      `An error occurred while extracting the configurations ${err}`
    );
    return null;
  }
};

export function arraysAreEqual(arr1: any, arr2: any) {
  if (arr1?.length !== arr2?.length) {
    return false;
  }

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  return true;
}

export function getInitials(sentence = "") {
  const words = sentence.split(" ");

  if (words.length === 1) {
    return words[0].charAt(0);
  }

  return words[0].charAt(0) + words[1].charAt(0);
}

export const isWindowAvailable = typeof window !== "undefined";

// Safe localStorage helpers to handle SecurityError
export const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn(`localStorage.getItem failed for key "${key}":`, error);
      return null;
    }
  },
  setItem: (key: string, value: string): boolean => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.warn(`localStorage.setItem failed for key "${key}":`, error);
      return false;
    }
  },
  removeItem: (key: string): boolean => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`localStorage.removeItem failed for key "${key}":`, error);
      return false;
    }
  },
  isAvailable: (): boolean => {
    try {
      const testKey = "__test_localStorage__";
      localStorage.setItem(testKey, "test");
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  },
};

export const generateColorShade = (
  primaryColor: PaletteColor,
  opacity: number = 0.16
): string => {
  return `${alpha(primaryColor.main, opacity)}`;
};

export function convertDateFormat(dateString: string) {
  const date = new Date(dateString);
  const timestamp = date.getTime();

  return timestamp.toString();
}

export const incrementStringNumber = (
  numString: string = "",
  incrementBy: number = 1
): string => {
  const num = parseInt(numString, 10);
  const incrementedNum = num + incrementBy;
  const incrementedString = String(incrementedNum);
  return incrementedString;
};

export function generateQueryString(params: Record<string, string>): string {
  const queryString = Object.entries(params)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");
  return queryString;
}
export function getQueryParams(queryString: string) {
  const params = new URLSearchParams(queryString);
  const queryParams: any = {};
  for (let [key, value] of params.entries()) {
    queryParams[key] = value;
  }

  return queryParams;
}

export function addQueryParamsToUrl(
  url: string,
  paramsList: { param: string; value: string }[]
): string {
  try {
    const urlObj = new URL(url);
    const params = urlObj.searchParams;

    paramsList.forEach(({ param, value }) => {
      params.set(param, value);
    });

    // Return the updated URL
    return urlObj.toString();
  } catch (error) {
    console.error("Invalid URL:", error, { url, paramsList });
    return url;
  }
}

export function generateUniqueId() {
  const useSecureRandom =
    typeof crypto !== "undefined" &&
    typeof crypto.getRandomValues === "function";

  function getRandomValue() {
    if (useSecureRandom) {
      // Generate a secure random number using crypto
      const randomBuffer = new Uint8Array(1);
      crypto.getRandomValues(randomBuffer);
      return randomBuffer[0] % 16; // Return a value between 0 and 15
    }
    // Fallback to Math.random() if crypto is unavailable
    return (Math.random() * 16) | 0; // Return a value between 0 and 15
  }

  const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = getRandomValue();
    const v = c === "x" ? r : (r & 0x3) | 0x8; // Set the version to 4 and the variant to 8, 9, A, or B
    return v.toString(16);
  });

  return uuid;
}

export function filterBlogAndVideoEntries(
  entries: any[],
  isMobile: boolean,
  isInitial: boolean = false
) {
  const videoEntries: any[] = [];
  const blogEntries: any[] = [];
  const otherEntries: any[] = [];

  if (isInitial) {
    const multimediaData: any = [];
    const responseData: any = [];
    entries.forEach((entry) => {
      if (["blog", "video"].includes(entry.type)) {
        multimediaData.unshift(entry); // Add the entry to the beginning of the filteredEntries array
      } else {
        responseData.push(entry);
      }
    });
    return {
      multimediaData: multimediaData.length > 0 ? multimediaData : null,
      responseData: responseData.length > 0 ? responseData : null,
    };
  }
  entries.forEach((entry) => {
    if (entry.type === "video") {
      videoEntries.push(entry);
    } else if (entry.type === "blog") {
      blogEntries.push(entry);
    } else {
      otherEntries.push(entry);
    }
  });

  // const multimediaData = [...videoEntries, ...blogEntries];
  let multimediaData: any[] = [];

  if (isMobile) {
    if (videoEntries.length > 0) {
      multimediaData = videoEntries;
    } else if (blogEntries.length > 0) {
      multimediaData = blogEntries;
    }
  } else {
    multimediaData = [...videoEntries, ...blogEntries];
  }

  // Return the object containing multimediaData and responseData
  return {
    multimediaData: multimediaData.length > 0 ? multimediaData : null,
    responseData: otherEntries.length > 0 ? otherEntries : null,
  };
}

export const sortMessages1 = (messages: any, isMobile: boolean) => {
  // Priority map for known types
  const typePriority: Record<string, number> = {
    video: 1,
    blog: 2,
    // Other types are implicitly assigned a higher priority (default is 999)
  };

  // Create a map to group messages by turn_id
  const groupedMessages = messages.reduce((acc: any, message: any) => {
    if (!acc[message.turn_id]) acc[message.turn_id] = [];
    acc[message.turn_id].push(message);
    return acc;
  }, {});

  Object.keys(groupedMessages).forEach((turnId) => {
    const group = groupedMessages[turnId];
    const hasBlog = group.some((msg: any) => msg.type === "blog");
    const hasVideo = group.some((msg: any) => msg.type === "video");

    if (isMobile && hasBlog && hasVideo) {
      groupedMessages[turnId] = group.filter((msg: any) => msg.type !== "blog");
    } else {
      groupedMessages[turnId].sort((a: any, b: any) => {
        const aPriority = typePriority[a.type] || 999; // Default high number for unknown types
        const bPriority = typePriority[b.type] || 999;
        return aPriority - bPriority;
      });
    }
  });

  // Flatten the grouped messages back into a single array, preserving the original turn_id order
  return Object.keys(groupedMessages).flatMap(
    (turnId) => groupedMessages[turnId]
  );
};

export const getValidMode = (mode: string) => {
  return VALID_MODES.includes(mode) ? mode : "published";
};

interface SalaryObject {
  currency?: string | null;
  unit?: string | null;
  minSalary?: number | null;
  maxSalary?: number | null;
}

export const formatSalary = (
  salary: SalaryObject | null | undefined
): string => {
  if (!salary) return "";

  const { currency = "", unit = "", minSalary, maxSalary } = salary;

  const formatNumber = (num: number | null | undefined) =>
    num != null && !isNaN(num) ? num.toLocaleString() : "";

  const formatCurrency = (curr: string | null) =>
    curr?.trim().toUpperCase() || "";
  const currencyPrefix = formatCurrency(currency)
    ? `${formatCurrency(currency)} `
    : "";
  const parts: string[] = [];

  const min = formatNumber(minSalary);
  const max = formatNumber(maxSalary);

  if (min && max) {
    parts.push(`${currencyPrefix}${min} - ${currencyPrefix}${max}`);
  } else if (min) {
    parts.push(`${currencyPrefix}${min}`);
  } else if (max) {
    parts.push(`${currencyPrefix}${max}`);
  }

  const salaryText = parts.join(" - ");

  if (unit?.trim()) {
    return salaryText ? `${salaryText} (${unit.trim().toLowerCase()})` : "";
  }

  return salaryText;
};
